/**
 * Verification Service
 * Handles the dual verification system (user email verification + admin approval).
 *
 * SECURITY: the privileged work — looking a user up by token and flipping
 * `email_verified` / `admin_approved` / `status` — runs SERVER-SIDE in Directus
 * Flows, NOT in the browser. Previously this used a bundled VITE_DIRECTUS_TOKEN
 * to PATCH /users/{id} directly from the client; because every VITE_* value ships
 * in the public JS bundle, anyone could have extracted that token and
 * self-approved/activated any account. The client now only POSTs the opaque
 * token to an unauthenticated Flow trigger (same pattern as emailFlowService);
 * the Flow validates it and performs the update with server-side credentials.
 *
 * Required Directus Flows (set their IDs in the frontend .env):
 *   VITE_FLOW_VERIFY_EMAIL_CONFIRM  — accepts { token }, marks email_verified,
 *                                     activates if already admin_approved.
 *   VITE_FLOW_ADMIN_APPROVE         — accepts { token }, marks admin_approved,
 *                                     activates if email already verified.
 * Each Flow should respond with a VerificationResult-shaped JSON body.
 */
import { directus } from '@/lib/directus';
import { readMe } from '@directus/sdk';

const DIRECTUS_URL = import.meta.env.VITE_DIRECTUS_URL || 'http://localhost:8065';
const FLOW_VERIFY_EMAIL_CONFIRM = import.meta.env.VITE_FLOW_VERIFY_EMAIL_CONFIRM;
const FLOW_ADMIN_APPROVE = import.meta.env.VITE_FLOW_ADMIN_APPROVE;

export interface VerificationResult {
  success: boolean;
  message: string;
  userActivated: boolean;
  needsAdminApproval?: boolean;
  needsEmailVerification?: boolean;
}

/**
 * POST an opaque token to a Directus Flow trigger and normalize the response
 * into a VerificationResult. No Authorization header — the Flow runs with its
 * own server-side credentials, so no privileged token is exposed to the client.
 */
async function triggerVerificationFlow(
  flowId: string | undefined,
  token: string,
  fallbackErrorMessage: string
): Promise<VerificationResult> {
  if (!flowId) {
    if (import.meta.env.DEV) {
      console.error('Verification flow id is not configured (.env).');
    }
    return { success: false, message: fallbackErrorMessage, userActivated: false };
  }

  try {
    const response = await fetch(`${DIRECTUS_URL}/flows/trigger/${flowId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });

    // Flows may wrap the payload in { data: ... }; accept either shape.
    const raw = await response.json().catch(() => ({}));
    const result = (raw && typeof raw === 'object' && 'data' in raw ? raw.data : raw) as Partial<VerificationResult>;

    if (!response.ok || !result || typeof result.success !== 'boolean') {
      return { success: false, message: result?.message || fallbackErrorMessage, userActivated: false };
    }
    return {
      success: result.success,
      message: result.message || '',
      userActivated: Boolean(result.userActivated),
      needsAdminApproval: result.needsAdminApproval,
      needsEmailVerification: result.needsEmailVerification,
    };
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Error triggering verification flow:', error);
    }
    return { success: false, message: fallbackErrorMessage, userActivated: false };
  }
}

/**
 * Verify user email with token (server-side Flow does the privileged update).
 */
export async function verifyUserEmail(token: string): Promise<VerificationResult> {
  return triggerVerificationFlow(
    FLOW_VERIFY_EMAIL_CONFIRM,
    token,
    'Erro ao verificar email. Por favor tente novamente.'
  );
}

/**
 * Approve user by admin (server-side Flow does the privileged update).
 */
export async function approveUser(token: string): Promise<VerificationResult> {
  return triggerVerificationFlow(
    FLOW_ADMIN_APPROVE,
    token,
    'Erro ao aprovar utilizador. Por favor tente novamente.'
  );
}

/**
 * Check if the CURRENT (logged-in) user can make purchases. Reads the caller's
 * OWN record via their session (/users/me) — no admin token, and no ability to
 * probe arbitrary user ids. `userId` is accepted for backwards compatibility but
 * the check is always scoped to the authenticated session.
 */
export async function canUserPurchase(_userId?: string): Promise<boolean> {
  try {
    const me = (await directus.request(
      readMe({ fields: ['status', 'email_verified', 'admin_approved'] as never })
    )) as unknown as { status?: string; email_verified?: boolean; admin_approved?: boolean };

    return (
      me?.status === 'active' &&
      me?.email_verified === true &&
      me?.admin_approved === true
    );
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Error checking purchase permission:', error);
    }
    return false;
  }
}
